import { Schema } from "effect";
import { ConflictedCandidateComposition } from "../candidate-composition-model.ts";
import { CandidateManifestResult } from "../development/candidate-manifest/model.ts";

// Failure data is shared; the backend retains the orchestration that produces it.
// A status of 500 preserves the existing generic HTTP boundary for these errors.

export const ConflictResolutionFailureCode = /* @__PURE__ */ (() => Schema.Literals(["duplicate-conflict", "duplicate-resolution", "extra-resolution", "incomplete-resolution-set", "mixed-generation", "stale-resolution", "tampered-conflict", "tampered-resolution"]))();
export type ConflictResolutionFailureCode = typeof ConflictResolutionFailureCode.Type;
const ConflictResolutionErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, code: ConflictResolutionFailureCode, status: Schema.tag(500) }))();
type ConflictResolutionErrorProperties = typeof ConflictResolutionErrorFields["~type.make.in"];

export const ConflictResolutionError = /* @__PURE__ */ (() => {
  class ConflictResolutionError extends Schema.TaggedError<ConflictResolutionError>()("ConflictResolutionError", ConflictResolutionErrorFields) {
    constructor(code: ConflictResolutionFailureCode, message: string);
    constructor(properties: ConflictResolutionErrorProperties, options?: Schema.MakeOptions);
    constructor(code: ConflictResolutionFailureCode | ConflictResolutionErrorProperties, message?: string | Schema.MakeOptions) {
      super(typeof code === "string" ? { code, message: typeof message === "string" ? message : "" } : code,
        typeof message === "object" ? message : undefined);
      this.name = "ConflictResolutionError";
    }

    toJSON() { return { name: this.name, code: this.code }; }
  }
  return ConflictResolutionError;
})();
export type ConflictResolutionError = InstanceType<typeof ConflictResolutionError>;

export const CandidateTargetRefreshErrorCode = /* @__PURE__ */ (() => Schema.Literals(["inputs-changed", "tampered-previous", "generation-exhausted", "revision-regressed", "same-revision-divergence", "target-unchanged"]))();
export type CandidateTargetRefreshErrorCode = typeof CandidateTargetRefreshErrorCode.Type;
const CandidateTargetRefreshErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, code: CandidateTargetRefreshErrorCode, status: Schema.tag(500) }))();
type CandidateTargetRefreshErrorProperties = typeof CandidateTargetRefreshErrorFields["~type.make.in"];

export const CandidateTargetRefreshError = /* @__PURE__ */ (() => {
  class CandidateTargetRefreshError extends Schema.TaggedError<CandidateTargetRefreshError>()("CandidateTargetRefreshError", CandidateTargetRefreshErrorFields) {
    constructor(code: CandidateTargetRefreshErrorCode, message: string);
    constructor(properties: CandidateTargetRefreshErrorProperties, options?: Schema.MakeOptions);
    constructor(code: CandidateTargetRefreshErrorCode | CandidateTargetRefreshErrorProperties, message?: string | Schema.MakeOptions) {
      super(typeof code === "string" ? { code, message: typeof message === "string" ? message : "" } : code,
        typeof message === "object" ? message : undefined);
      this.name = "CandidateTargetRefreshError";
    }

    toJSON() { return { name: this.name, code: this.code }; }
  }
  return CandidateTargetRefreshError;
})();
export type CandidateTargetRefreshError = InstanceType<typeof CandidateTargetRefreshError>;

export const GateAuthorityErrorCode = /* @__PURE__ */ (() => Schema.Literals(["expired", "insufficient-authority", "invalid-lifetime", "revoked-grant", "tampered-artifact"]))();
export type GateAuthorityErrorCode = typeof GateAuthorityErrorCode.Type;
const GateAuthorityErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, code: GateAuthorityErrorCode, status: Schema.tag(500) }))();
type GateAuthorityErrorProperties = typeof GateAuthorityErrorFields["~type.make.in"];

export const GateAuthorityError = /* @__PURE__ */ (() => {
  class GateAuthorityError extends Schema.TaggedError<GateAuthorityError>()("GateAuthorityError", GateAuthorityErrorFields) {
    constructor(code: GateAuthorityErrorCode, message: string);
    constructor(properties: GateAuthorityErrorProperties, options?: Schema.MakeOptions);
    constructor(code: GateAuthorityErrorCode | GateAuthorityErrorProperties, message?: string | Schema.MakeOptions) {
      super(typeof code === "string" ? { code, message: typeof message === "string" ? message : "" } : code,
        typeof message === "object" ? message : undefined);
      this.name = "GateAuthorityError";
    }

    toJSON() { return { name: this.name, code: this.code }; }
  }
  return GateAuthorityError;
})();
export type GateAuthorityError = InstanceType<typeof GateAuthorityError>;

export const PublicationAuthorizationErrorCode = /* @__PURE__ */ (() => Schema.Literals(["gate-not-ready", "grant-mismatch", "invalid-lifetime", "revoked-grant", "stale-gate", "tampered-artifact"]))();
export type PublicationAuthorizationErrorCode = typeof PublicationAuthorizationErrorCode.Type;
const PublicationAuthorizationErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, code: PublicationAuthorizationErrorCode, status: Schema.tag(500) }))();
type PublicationAuthorizationErrorProperties = typeof PublicationAuthorizationErrorFields["~type.make.in"];

export const PublicationAuthorizationError = /* @__PURE__ */ (() => {
  class PublicationAuthorizationError extends Schema.TaggedError<PublicationAuthorizationError>()("PublicationAuthorizationError", PublicationAuthorizationErrorFields) {
    constructor(code: PublicationAuthorizationErrorCode, message: string);
    constructor(properties: PublicationAuthorizationErrorProperties, options?: Schema.MakeOptions);
    constructor(code: PublicationAuthorizationErrorCode | PublicationAuthorizationErrorProperties, message?: string | Schema.MakeOptions) {
      super(typeof code === "string" ? { code, message: typeof message === "string" ? message : "" } : code,
        typeof message === "object" ? message : undefined);
      this.name = "PublicationAuthorizationError";
    }

    toJSON() { return { name: this.name, code: this.code }; }
  }
  return PublicationAuthorizationError;
})();
export type PublicationAuthorizationError = InstanceType<typeof PublicationAuthorizationError>;

export const PublicationTransactionErrorCode = /* @__PURE__ */ (() => Schema.Literals(["authorization-expired", "authorization-not-found", "authorization-protocol-obsolete", "authorization-revoked", "candidate-mismatch", "gate-revoked", "insufficient-authority", "repository-frozen", "stale-grant", "target-moved", "unknown-merge-receipt"]))();
export type PublicationTransactionErrorCode = typeof PublicationTransactionErrorCode.Type;
const PublicationTransactionErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, code: PublicationTransactionErrorCode, status: Schema.tag(500) }))();
type PublicationTransactionErrorProperties = typeof PublicationTransactionErrorFields["~type.make.in"];

export const PublicationTransactionError = /* @__PURE__ */ (() => {
  class PublicationTransactionError extends Schema.TaggedError<PublicationTransactionError>()("PublicationTransactionError", PublicationTransactionErrorFields) {
    constructor(code: PublicationTransactionErrorCode, message: string);
    constructor(properties: PublicationTransactionErrorProperties, options?: Schema.MakeOptions);
    constructor(code: PublicationTransactionErrorCode | PublicationTransactionErrorProperties, message?: string | Schema.MakeOptions) {
      super(typeof code === "string" ? { code, message: typeof message === "string" ? message : "" } : code,
        typeof message === "object" ? message : undefined);
      this.name = "PublicationTransactionError";
    }

    toJSON() { return { name: this.name, code: this.code }; }
  }
  return PublicationTransactionError;
})();
export type PublicationTransactionError = InstanceType<typeof PublicationTransactionError>;

const GateReportIntegrityErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, status: Schema.tag(500) }))();
type GateReportIntegrityErrorProperties = typeof GateReportIntegrityErrorFields["~type.make.in"];

export const GateReportIntegrityError = /* @__PURE__ */ (() => {
  class GateReportIntegrityError extends Schema.TaggedError<GateReportIntegrityError>()("GateReportIntegrityError", GateReportIntegrityErrorFields) {
    constructor(message: string | GateReportIntegrityErrorProperties, options?: Schema.MakeOptions) {
      super(typeof message === "string" ? { message } : message, options);
      this.name = "GateReportIntegrityError";
    }

    toJSON() { return { name: this.name }; }
  }
  return GateReportIntegrityError;
})();
export type GateReportIntegrityError = InstanceType<typeof GateReportIntegrityError>;

const PublicationRevocationPendingErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, status: Schema.tag(500), code: Schema.tag("publication-revocation-pending") }))();
type PublicationRevocationPendingErrorProperties = typeof PublicationRevocationPendingErrorFields["~type.make.in"];

export const PublicationRevocationPendingError = /* @__PURE__ */ (() => {
  class PublicationRevocationPendingError extends Schema.TaggedError<PublicationRevocationPendingError>()("PublicationRevocationPendingError", PublicationRevocationPendingErrorFields) {
    constructor(message: string | PublicationRevocationPendingErrorProperties = "Publication revocation has not finished.", options?: Schema.MakeOptions) {
      super(typeof message === "string" ? { message } : message, options);
      this.name = "PublicationRevocationPendingError";
    }

    toJSON() { return { name: this.name, code: this.code }; }
  }
  return PublicationRevocationPendingError;
})();
export type PublicationRevocationPendingError = InstanceType<typeof PublicationRevocationPendingError>;

const PublicationGateEvaluationRequiredErrorFields = /* @__PURE__ */ (() => Schema.Struct({ message: Schema.String, status: Schema.tag(500), code: Schema.tag("publication-gate-evaluation-required") }))();
type PublicationGateEvaluationRequiredErrorProperties = typeof PublicationGateEvaluationRequiredErrorFields["~type.make.in"];

export const PublicationGateEvaluationRequiredError = /* @__PURE__ */ (() => {
  class PublicationGateEvaluationRequiredError extends Schema.TaggedError<PublicationGateEvaluationRequiredError>()("PublicationGateEvaluationRequiredError", PublicationGateEvaluationRequiredErrorFields) {
    constructor(message: string | PublicationGateEvaluationRequiredErrorProperties = "Publication requires a current coordinator gate evaluation.", options?: Schema.MakeOptions) {
      super(typeof message === "string" ? { message } : message, options);
      this.name = "PublicationGateEvaluationRequiredError";
    }

    toJSON() { return { name: this.name, code: this.code }; }
  }
  return PublicationGateEvaluationRequiredError;
})();
export type PublicationGateEvaluationRequiredError = InstanceType<typeof PublicationGateEvaluationRequiredError>;

const GateMutationFields = /* @__PURE__ */ (() => Schema.Struct({
  message: Schema.String, status: Schema.tag(500),
  code: Schema.tag("publication-gate-mutation-aborted"), operationId: Schema.String
}))();
type GateMutationProperties = typeof GateMutationFields["~type.make.in"];

export const PublicationGateMutationAbortedError = /* @__PURE__ */ (() => {
  class PublicationGateMutationAbortedError extends Schema.TaggedError<PublicationGateMutationAbortedError>()(
    "PublicationGateMutationAbortedError", GateMutationFields
  ) {
    constructor(operationId: string | GateMutationProperties, options?: Schema.MakeOptions) {
      super(typeof operationId === "string" ? {
        operationId, message: `Publication gate mutation ${operationId} was durably aborted.`
      } : operationId, options);
      this.name = "PublicationGateMutationAbortedError";
    }

    toJSON() { return { name: this.name, code: this.code, operationId: this.operationId }; }
  }
  return PublicationGateMutationAbortedError;
})();
export type PublicationGateMutationAbortedError = InstanceType<typeof PublicationGateMutationAbortedError>;

const DispatchFields = /* @__PURE__ */ (() => Schema.Struct({
  message: Schema.String, status: Schema.tag(409), code: Schema.tag("indeterminate-effect"),
  operationId: Schema.String, runId: Schema.String
}))();
type DispatchProperties = typeof DispatchFields["~type.make.in"];

/** Raised when a non-reconcilable uncertain effect blocks retry or success. */
export const IndeterminateDispatchError = /* @__PURE__ */ (() => {
  class IndeterminateDispatchError extends Schema.TaggedError<IndeterminateDispatchError>()(
    "IndeterminateDispatchError", DispatchFields
  ) {
    constructor(operationId: string, runId: string, action: string);
    constructor(properties: DispatchProperties, options?: Schema.MakeOptions);
    constructor(operationId: string | DispatchProperties, runId?: string | Schema.MakeOptions, action?: string) {
      super(typeof operationId === "string" ? {
        operationId, runId: typeof runId === "string" ? runId : "",
        // Keep the persisted/wire diagnostic vocabulary until its explicit migration.
        message: `Effect ${operationId} is indeterminate and blocks automatic ${action}; ` +
          "an authorized reconciliation decision is required."
      } : operationId, typeof runId === "object" ? runId : undefined);
      this.name = "IndeterminateDispatchError";
    }

    toJSON() {
      return { code: this.code, status: this.status, name: this.name, operationId: this.operationId, runId: this.runId };
    }
  }
  return IndeterminateDispatchError;
})();
export type IndeterminateDispatchError = InstanceType<typeof IndeterminateDispatchError>;

const TargetMovedFields = /* @__PURE__ */ (() => Schema.Struct({
  message: Schema.String, status: Schema.tag(500),
  expectedCommitId: Schema.String, expectedRevision: Schema.Number,
  observedCommitId: Schema.NullOr(Schema.String), observedRevision: Schema.Number
}))();
type TargetMovedProperties = typeof TargetMovedFields["~type.make.in"];
export const ObservedCandidateTargetRef = /* @__PURE__ */ (() => Schema.Struct({ commitId: Schema.String, revision: Schema.Number }))();
export type ObservedCandidateTargetRef = typeof ObservedCandidateTargetRef.Type;

export const CandidateTargetMovedError = /* @__PURE__ */ (() => {
  class CandidateTargetMovedError extends Schema.TaggedError<CandidateTargetMovedError>()(
    "CandidateTargetMovedError", TargetMovedFields
  ) {
    constructor(expectedCommitId: string, expectedRevision: number, observed: ObservedCandidateTargetRef | null);
    constructor(properties: TargetMovedProperties, options?: Schema.MakeOptions);
    constructor(expectedCommitId: string | TargetMovedProperties, expectedRevision?: number | Schema.MakeOptions, observed?: ObservedCandidateTargetRef | null) {
      super(typeof expectedCommitId === "string" ? {
        expectedCommitId, expectedRevision: typeof expectedRevision === "number" ? expectedRevision : 0,
        observedCommitId: observed?.commitId ?? null, observedRevision: observed?.revision ?? 0,
        message: `Candidate target moved: expected ${expectedCommitId} at revision ${expectedRevision}, observed ${observed?.commitId ?? "no ref"} at revision ${observed?.revision ?? 0}.`
      } : expectedCommitId, typeof expectedRevision === "object" ? expectedRevision : undefined);
      this.name = "CandidateTargetMovedError";
    }

    toJSON() {
      return { name: this.name, expectedCommitId: this.expectedCommitId, expectedRevision: this.expectedRevision,
        observedCommitId: this.observedCommitId, observedRevision: this.observedRevision };
    }
  }
  return CandidateTargetMovedError;
})();
export type CandidateTargetMovedError = InstanceType<typeof CandidateTargetMovedError>;

const CandidateCompositionConflictFields = /* @__PURE__ */ (() => Schema.Struct({
  message: Schema.String, status: Schema.tag(500), manifest: CandidateManifestResult,
  composition: ConflictedCandidateComposition
}))();
type CandidateCompositionConflictProperties = typeof CandidateCompositionConflictFields["~type.make.in"];
export const CandidateCompositionConflictError = /* @__PURE__ */ (() => {
  class CandidateCompositionConflictError extends Schema.TaggedError<CandidateCompositionConflictError>()("CandidateCompositionConflictError", CandidateCompositionConflictFields) {
    declare readonly manifest: CandidateManifestResult;
    declare readonly composition: ConflictedCandidateComposition;
    constructor(manifest: CandidateManifestResult, composition: ConflictedCandidateComposition);
    constructor(properties: CandidateCompositionConflictProperties, options?: Schema.MakeOptions);
    constructor(manifest: CandidateManifestResult | CandidateCompositionConflictProperties, composition?: ConflictedCandidateComposition | Schema.MakeOptions) {
      if ("candidateId" in manifest) {
        const result = composition as ConflictedCandidateComposition;
        super({ manifest, composition: result,
          message: `Candidate ${manifest.candidateId} has ${result.conflicts.length} unresolved conflict${result.conflicts.length === 1 ? "" : "s"}.` });
        // Validation completed above; retain the original immutable artifacts.
        this.manifest = manifest;
        this.composition = result;
      } else {
        super(manifest, composition as Schema.MakeOptions | undefined);
      }
      this.name = "CandidateCompositionConflictError";
    }
    toJSON() { return { name: this.name, manifest: this.manifest, composition: this.composition }; }
  }
  return CandidateCompositionConflictError;
})();
export type CandidateCompositionConflictError = InstanceType<typeof CandidateCompositionConflictError>;

export const DevelopmentFailure = /* @__PURE__ */ (() => Schema.Union([
  ConflictResolutionError, CandidateTargetRefreshError, CandidateTargetMovedError, GateAuthorityError,
  GateReportIntegrityError, PublicationAuthorizationError, PublicationTransactionError,
  PublicationRevocationPendingError, PublicationGateMutationAbortedError, PublicationGateEvaluationRequiredError,
  IndeterminateDispatchError, CandidateCompositionConflictError
]))();
export type DevelopmentFailure = typeof DevelopmentFailure.Type;
